/**
 * Stage 2: Parsing
 * Progressive sentence-by-sentence display during AI analysis.
 * Sentences with `translation` show full content; others show skeleton cards.
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CourseDraft,
  VocabularyEntry,
  ComprehensiveVocabularyEntry,
  AnnotatedSentence,
  PhraseEntry,
  QuizQuestion,
} from '../../../types/creator-workflow';
import { ClickableText } from '../../ClickableWord';
import ProgressBar from '../ProgressBar';
import { useProgressPolling } from '../../../hooks/useProgressPolling';
import './Stage2Parsing.css';

interface Stage2ParsingProps {
  draft: CourseDraft;
  onParseContent: () => Promise<void>;
  onAdvanceStage: () => Promise<void>;
  onGoBackStage: () => Promise<void>;
  onRefreshDraft?: () => Promise<void>;
  onUpdateDraft?: (data: any) => Promise<void>;
  loading: boolean;
}

// Skeleton card component for sentences not yet analyzed
const SentenceSkeletonCard: React.FC<{ index: number; text: string }> = ({ index, text }) => (
  <div style={{
    marginBottom: '1.5rem',
    padding: '1.5rem',
    backgroundColor: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '8px',
    opacity: 0.7,
  }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.75rem' }}>
      <span style={{
        display: 'inline-block',
        padding: '4px 10px',
        backgroundColor: '#ced4da',
        color: 'white',
        borderRadius: '4px',
        fontSize: '13px',
        fontWeight: 'bold',
        flexShrink: 0,
      }}>
        #{index + 1}
      </span>
      <p style={{ fontSize: '15px', color: '#495057', margin: 0, lineHeight: '1.6' }}>
        {text}
      </p>
    </div>
    {/* Skeleton shimmer lines */}
    <div style={{ paddingLeft: '0.5rem' }}>
      <div style={{
        height: '14px',
        backgroundColor: '#dee2e6',
        borderRadius: '4px',
        marginBottom: '0.5rem',
        width: '80%',
        animation: 'pulse 1.5s ease-in-out infinite',
      }} />
      <div style={{
        height: '14px',
        backgroundColor: '#dee2e6',
        borderRadius: '4px',
        width: '60%',
        animation: 'pulse 1.5s ease-in-out infinite',
      }} />
    </div>
  </div>
);

// Analyzed sentence card
const SentenceCard: React.FC<{ sentence: AnnotatedSentence; index: number }> = ({ sentence, index }) => {
  const { t } = useTranslation('creator');
  return (
  <div style={{
    marginBottom: '1.5rem',
    padding: '1.5rem',
    backgroundColor: 'white',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    animation: 'fadeIn 0.4s ease-in',
  }}>
    <div style={{ marginBottom: '0.75rem' }}>
      <span style={{
        display: 'inline-block',
        padding: '4px 10px',
        backgroundColor: '#667eea',
        color: 'white',
        borderRadius: '4px',
        fontSize: '13px',
        fontWeight: 'bold',
        marginRight: '0.75rem',
      }}>
        #{index + 1}
      </span>
      {sentence.speaker && (
        <span style={{
          display: 'inline-block',
          padding: '4px 10px',
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: 'bold',
        }}>
          🎭 {sentence.speaker}
        </span>
      )}
    </div>
    <p style={{ fontSize: '16px', fontWeight: '500', color: '#333', marginBottom: '0.75rem', lineHeight: '1.8' }}>
      <ClickableText text={sentence.text} />
    </p>
    <p style={{ fontSize: '15px', color: '#555', lineHeight: '1.8', backgroundColor: '#f8f9fa', padding: '0.75rem', borderRadius: '6px', marginBottom: '0.75rem' }}>
      {sentence.translation}
    </p>

    {sentence.grammar_points && sentence.grammar_points.length > 0 && (
      <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #f0f0f0' }}>
        <p style={{ fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '0.5rem' }}>
          {t('workflow.stage2.grammarPoints')}
        </p>
        {sentence.grammar_points.map((gp, gpIdx) => (
          <div key={gpIdx} style={{
            marginBottom: '0.5rem',
            padding: '0.5rem 0.75rem',
            backgroundColor: '#fffef0',
            borderLeft: '3px solid #ffc107',
            borderRadius: '4px',
          }}>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '0.2rem' }}>
              <strong>{t('workflow.stage2.grammarEn')}</strong> {gp.explanation_en}
            </p>
            <p style={{ fontSize: '13px', color: '#666' }}>
              <strong>{t('workflow.stage2.grammarNative')}</strong> {gp.explanation_native}
            </p>
          </div>
        ))}
      </div>
    )}

    {sentence.cultural_notes && (
      <div style={{
        marginTop: '0.75rem',
        padding: '0.5rem 0.75rem',
        backgroundColor: '#f0f8ff',
        borderLeft: '3px solid #2196F3',
        borderRadius: '4px',
      }}>
        <p style={{ fontSize: '13px', color: '#555', marginBottom: '0.2rem' }}>
          <strong>{t('workflow.stage2.culturalNoteEn')}</strong> {sentence.cultural_notes.note_en}
        </p>
        <p style={{ fontSize: '13px', color: '#555' }}>
          <strong>{t('workflow.stage2.culturalNoteNative')}</strong> {sentence.cultural_notes.note_native}
        </p>
      </div>
    )}
  </div>
  );
};

// Language code to display name mapping
const LANGUAGE_NAMES: Record<string, string> = {
  'zh-TW': '繁體中文',
  'zh-CN': '简体中文',
  'ja': '日本語',
  'ko': '한국어',
  'th': 'ภาษาไทย',
  'vi': 'Tiếng Việt',
  'id': 'Bahasa Indonesia',
  'ms': 'Bahasa Melayu',
};

const Stage2Parsing: React.FC<Stage2ParsingProps> = ({
  draft,
  onParseContent,
  onAdvanceStage,
  onGoBackStage,
  onRefreshDraft,
  onUpdateDraft,
  loading,
}) => {
  const { t } = useTranslation(['creator', 'common']);

  const [showReParseConfirm, setShowReParseConfirm] = useState(false);
  const [localNativeTitle, setLocalNativeTitle] = useState(draft.title_native || '');

  // Sync native title when draft changes (e.g., after parsing completes)
  useEffect(() => {
    if (draft.title_native) {
      setLocalNativeTitle(draft.title_native);
    }
  }, [draft.title_native]);

  // Save native title to backend (on blur)
  // Also sync the titles JSONB so publish picks up the edited value
  const handleNativeTitleBlur = useCallback(async () => {
    if (localNativeTitle !== draft.title_native && onUpdateDraft) {
      const updatedTitles = { ...(draft.titles || {}) };
      if (draft.translation_language) {
        updatedTitles[draft.translation_language] = localNativeTitle;
      }
      await onUpdateDraft({ title_native: localNativeTitle, titles: updatedTitles });
    }
  }, [localNativeTitle, draft.title_native, draft.titles, draft.translation_language, onUpdateDraft]);

  // Local sentences state — updated by progressive polling
  const [localSentences, setLocalSentences] = useState<AnnotatedSentence[]>(
    (draft.sentences as AnnotatedSentence[]) || []
  );

  // Keep local sentences in sync when draft prop changes (e.g., on initial load)
  useEffect(() => {
    if (draft.sentences && draft.sentences.length > 0) {
      setLocalSentences(draft.sentences as AnnotatedSentence[]);
    }
  }, [draft.sentences]);

  const hasParsed = draft.vocabulary && draft.vocabulary.length > 0;
  const isParsingInProgress = draft.parsing_status === 'in_progress';
  const parseProgress = draft.stage_progress?.parsing;

  // Sentences that have been analyzed (have a translation)
  const analyzedCount = useMemo(
    () => localSentences.filter(s => s.translation && s.translation.length > 0).length,
    [localSentences]
  );
  const hasAnySentences = localSentences.length > 0;

  // Ref for auto-scrolling to results section
  const resultsSectionRef = useRef<HTMLDivElement>(null);

  // Poll parsing progress when parsing is in progress
  const shouldPoll = isParsingInProgress || (loading && !hasParsed);

  const handleDraftUpdate = useCallback((updatedDraft: CourseDraft) => {
    // Update local sentences progressively as each is analyzed
    if (updatedDraft.sentences && updatedDraft.sentences.length > 0) {
      setLocalSentences(updatedDraft.sentences as AnnotatedSentence[]);
    }
  }, []);

  const { progress: parsingProgress } = useProgressPolling({
    draftId: draft.id,
    stage: 'parsing',
    shouldPoll,
    onDraftUpdate: handleDraftUpdate,
    onComplete: () => {
      console.log('✅ Parsing completed! Refreshing draft...');
      // Reload the full draft so parent has the complete vocabulary/quiz data
      onRefreshDraft?.();
    },
    onError: (error) => {
      console.error('❌ Parsing error:', error);
    },
  });

  // Auto-scroll to sentences section when first sentence appears
  useEffect(() => {
    if (analyzedCount === 1 && resultsSectionRef.current) {
      setTimeout(() => {
        resultsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [analyzedCount]);

  // Handle re-parse with confirmation
  const handleReParse = () => setShowReParseConfirm(true);
  const confirmReParse = async () => {
    setShowReParseConfirm(false);
    setLocalSentences([]);
    await onParseContent();
  };
  const cancelReParse = () => setShowReParseConfirm(false);

  // Check if draft has comprehensive learning content (fully parsed)
  const hasComprehensiveContent = !!(
    draft.full_text_translation ||
    hasParsed ||
    (draft.phrases && draft.phrases.length > 0) ||
    (draft.quiz_questions && draft.quiz_questions.length > 0)
  );

  // Check if vocabulary has comprehensive structure
  const hasComprehensiveVocab = draft.vocabulary && draft.vocabulary.length > 0 &&
    'pronunciation' in draft.vocabulary[0];

  const getDifficultyColor = (difficulty: number): string => {
    if (difficulty <= 3) return '#28a745';
    if (difficulty <= 6) return '#ffc107';
    return '#dc3545';
  };

  const getDifficultyLabel = (difficulty: number): string => {
    if (difficulty <= 3) return 'Easy (A1-A2)';
    if (difficulty <= 6) return 'Medium (B1-B2)';
    return 'Hard (C1-C2)';
  };

  // Determine what to show in the progress area
  const showProgressSection = isParsingInProgress || (shouldPoll && !hasParsed);
  const showProgressiveSentences = hasAnySentences && (isParsingInProgress || hasParsed);

  return (
    <div className="stage-3-parsing">
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <header className="stage-header">
        <h2>{t('creator:workflow.stage2.title')}</h2>
        <p>{t('creator:workflow.stage2.description')}</p>
      </header>

      {/* Start Analysis section (shown when parsing has neither started nor
          completed). Defensive: also shows when sentences already exist but
          parsing wasn't auto-triggered (e.g. page reload between stages). */}
      {!hasParsed && !isParsingInProgress && (
        <div className="parsing-prompt">
          <div className="info-box">
            <h3>{t('creator:workflow.stage2.startAnalysis')}</h3>
            <p>{t('creator:workflow.stage2.startAnalysisDesc')}</p>
            <ul>
              <li>{t('creator:workflow.stage2.featureTranslation')}</li>
              <li>{t('creator:workflow.stage2.featureVocabulary')}</li>
              <li>{t('creator:workflow.stage2.featureSpeaking')}</li>
              <li>{t('creator:workflow.stage2.featureStructure')}</li>
              <li>{t('creator:workflow.stage2.featureQuiz')}</li>
            </ul>
            <p style={{ marginTop: '1rem', fontSize: '14px', color: '#666' }}>
              {t('creator:workflow.stage2.progressiveMode')}
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '14px', color: '#666' }}>
              {t('creator:workflow.stage2.targetCefrLevel', { level: draft.cefr_level })}
            </p>
          </div>

          <div className="form-actions">
            <button onClick={onGoBackStage} className="btn-secondary" disabled={loading}>
              {t('common:back')}
            </button>
            <button
              onClick={() => {
                console.log('🔘 Parse button clicked');
                onParseContent();
              }}
              className="btn-primary"
              disabled={loading}
            >
              {loading ? t('creator:workflow.stage2.analyzing') : t('creator:workflow.stage2.startAnalysis')}
            </button>
          </div>
        </div>
      )}

      {/* Progress bar — shown while parsing in progress */}
      {showProgressSection && (
        <div className="progress-section" style={{ marginBottom: '1.5rem' }}>
          {isParsingInProgress && analyzedCount === 0 && (
            <h4 style={{ marginBottom: '0.75rem' }}>{t('creator:workflow.stage2.analyzing')}</h4>
          )}
          {isParsingInProgress && analyzedCount > 0 && (
            <h4 style={{ marginBottom: '0.75rem' }}>
              {t('creator:workflow.stage2.sentencesAnalyzedProgress', { count: analyzedCount, total: localSentences.length })}
            </h4>
          )}
          <ProgressBar
            progressPercent={parsingProgress?.progress_percent ?? parseProgress?.progress_percent ?? 0}
            statusMessage={parsingProgress?.status_message ?? parseProgress?.status_message}
            statusKey={parsingProgress?.status_key ?? parseProgress?.status_key}
            statusParams={parsingProgress?.status_params ?? parseProgress?.status_params}
            currentItem={parsingProgress?.current_item ?? parseProgress?.current_item}
            totalItems={parsingProgress?.total_items ?? parseProgress?.total_items ?? localSentences.length}
          />
          {isParsingInProgress && (
            <p style={{ fontSize: '13px', color: '#666', marginTop: '0.5rem' }}>
              {t('creator:workflow.stage2.progressiveHint')}
            </p>
          )}
        </div>
      )}

      {/* Progressive sentence display — shown as soon as sentences start appearing */}
      {showProgressiveSentences && (
        <div ref={resultsSectionRef} style={{ marginTop: isParsingInProgress ? '0' : '1.5rem' }}>

          {/* Header with re-parse button (only when fully done) */}
          {hasParsed && (
            <div className="results-header">
              <div>
                <h3>{t('creator:workflow.stage2.title')}</h3>
                {parseProgress?.completed && (
                  <span className="success-badge">
                    ✅ {t('creator:workflow.stage2.analysisComplete')}
                  </span>
                )}
              </div>
              <button
                onClick={handleReParse}
                className="btn-secondary"
                disabled={loading}
                style={{ padding: '0.5rem 1rem', fontSize: '14px' }}
              >
                {t('creator:workflow.stage1.regenerate')}
              </button>
            </div>
          )}

          {/* Metrics (only when fully parsed) */}
          {hasParsed && parseProgress?.parse_results && (
            <div className="metrics-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="metric-card">
                <span className="metric-value">{localSentences.length}</span>
                <span className="metric-label">{t('creator:workflow.stage2.sentences')}</span>
              </div>
              <div className="metric-card">
                <span className="metric-value">{parseProgress.parse_results.vocabulary_count}</span>
                <span className="metric-label">{t('creator:workflow.stage2.keyVocabulary')}</span>
              </div>
              <div className="metric-card">
                <span className="metric-value">{parseProgress.parse_results.quiz_question_count || 0}</span>
                <span className="metric-label">{t('creator:workflow.stage2.quizQuestionsCountMetric')}</span>
              </div>
              <div className="metric-card">
                <span className="metric-value">{draft.cefr_level}</span>
                <span className="metric-label">{t('creator:workflow.stage2.estimatedCEFR')}</span>
              </div>
            </div>
          )}

          {/* Course Title Section (shown after parsing completes) */}
          {hasParsed && draft.title && (
            <div style={{
              marginBottom: '1.5rem',
              padding: '1.5rem',
              backgroundColor: '#f8f9fa',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
            }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '0.5rem' }}>
                  {t('creator:workflow.stage2.englishTitle')}
                </label>
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  fontSize: '15px',
                  color: '#333',
                }}>
                  {draft.title}
                </div>
              </div>
              {draft.translation_language && (
                <div>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '0.25rem' }}>
                    {t('creator:workflow.stage2.nativeTitle')} ({LANGUAGE_NAMES[draft.translation_language] || draft.translation_language})
                  </label>
                  <p style={{ fontSize: '12px', color: '#888', marginBottom: '0.5rem' }}>
                    {t('creator:workflow.stage2.editNativeTitle')}
                  </p>
                  <input
                    type="text"
                    value={localNativeTitle}
                    onChange={(e) => setLocalNativeTitle(e.target.value)}
                    onBlur={handleNativeTitleBlur}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      fontSize: '15px',
                      border: '1px solid #ccc',
                      borderRadius: '6px',
                      boxSizing: 'border-box',
                    }}
                    placeholder={t('creator:workflow.stage2.nativeTitle')}
                  />
                </div>
              )}
            </div>
          )}

          {/* Sentence-by-sentence results */}
          <details open style={{ marginBottom: '2rem' }}>
            <summary style={{
              cursor: 'pointer',
              padding: '1rem',
              backgroundColor: '#f0fff4',
              border: '1px solid #4CAF50',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '16px',
              color: '#333',
            }}>
              {t('creator:workflow.stage2.sentenceAnalysisSection', { done: analyzedCount, total: localSentences.length })}
            </summary>
            <div style={{ marginTop: '1rem' }}>
              {localSentences.map((sentence, idx) => (
                sentence.translation
                  ? <SentenceCard key={idx} sentence={sentence} index={idx} />
                  : <SentenceSkeletonCard key={idx} index={idx} text={sentence.text} />
              ))}
            </div>
          </details>

          {/* Full text translation (when available) */}
          {draft.full_text_translation && (
            <details style={{ marginBottom: '2rem' }}>
              <summary style={{
                cursor: 'pointer',
                padding: '1rem',
                backgroundColor: '#f0f8ff',
                border: '1px solid #667eea',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '16px',
                color: '#333',
              }}>
                {t('creator:workflow.stage2.fullTextTranslationSection')}
              </summary>
              <div style={{
                marginTop: '1rem',
                padding: '1.5rem',
                backgroundColor: '#fafafa',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
              }}>
                <p style={{ fontSize: '15px', lineHeight: '2', color: '#333', whiteSpace: 'pre-wrap' }}>
                  {draft.full_text_translation.translation}
                </p>
              </div>
            </details>
          )}

          {/* Enhanced Vocabulary (legacy comprehensive vocab) */}
          {hasComprehensiveVocab && (
            <details style={{ marginBottom: '2rem' }}>
              <summary style={{
                cursor: 'pointer',
                padding: '1rem',
                backgroundColor: '#fff9e6',
                border: '1px solid #ffc107',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '16px',
                color: '#333',
              }}>
                {t('creator:workflow.stage2.enhancedVocabularySection', { count: draft.vocabulary?.length || 0 })}
              </summary>
              <div style={{ marginTop: '1rem' }}>
                {(draft.vocabulary as ComprehensiveVocabularyEntry[])?.map((vocab, idx) => (
                  <div key={idx} style={{
                    marginBottom: '1.5rem',
                    padding: '1.5rem',
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                  }}>
                    <h4 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                      {vocab.word} <span style={{ fontSize: '16px', color: '#888', fontWeight: 'normal' }}>/{vocab.pronunciation}/</span>
                    </h4>
                    <p style={{ fontSize: '15px', color: '#333', marginBottom: '0.5rem' }}>
                      <strong>{t('creator:workflow.stage2.definitionEn')}</strong> {vocab.general_definition_en}
                    </p>
                    <p style={{ fontSize: '14px', color: '#666' }}>
                      <strong>{t('creator:workflow.stage2.definitionNative')}</strong>{vocab.general_definition}
                    </p>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Phrases (when available) */}
          {draft.phrases && draft.phrases.length > 0 && (
            <details style={{ marginBottom: '2rem' }}>
              <summary style={{
                cursor: 'pointer',
                padding: '1rem',
                backgroundColor: '#f3e5f5',
                border: '1px solid #9c27b0',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '16px',
                color: '#333',
              }}>
                {t('creator:workflow.stage2.phraseAnalysisSection', { count: draft.phrases.length })}
              </summary>
              <div style={{ marginTop: '1rem' }}>
                {draft.phrases.map((phrase: PhraseEntry, idx: number) => (
                  <div key={idx} style={{
                    marginBottom: '1.5rem',
                    padding: '1.5rem',
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                  }}>
                    <h4 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                      "{phrase.text}"
                    </h4>
                    <p style={{ fontSize: '15px', color: '#333', marginBottom: '0.5rem' }}>
                      <strong>{t('creator:workflow.stage2.definitionEn')}</strong> {phrase.definition_en}
                    </p>
                    <p style={{ fontSize: '14px', color: '#666' }}>
                      <strong>{t('creator:workflow.stage2.definitionNative')}</strong>{phrase.definition_native}
                    </p>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Quiz Questions (when available) */}
          {draft.quiz_questions && draft.quiz_questions.length > 0 && (
            <details style={{ marginBottom: '2rem' }}>
              <summary style={{
                cursor: 'pointer',
                padding: '1rem',
                backgroundColor: '#e8f5e9',
                border: '1px solid #4CAF50',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '16px',
                color: '#333',
              }}>
                {t('creator:workflow.stage2.quizQuestionsSection', { count: draft.quiz_questions.length })}
              </summary>
              <div style={{ marginTop: '1rem' }}>
                {draft.quiz_questions.map((quiz: QuizQuestion, idx: number) => (
                  <div key={idx} style={{
                    marginBottom: '1.5rem',
                    padding: '1.5rem',
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      <span style={{
                        padding: '4px 10px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: 'bold',
                      }}>Q{idx + 1}</span>
                      <span style={{
                        padding: '4px 10px',
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        borderRadius: '4px',
                        fontSize: '12px',
                      }}>{quiz.type.replace('_', ' ')}</span>
                      <span style={{
                        padding: '4px 10px',
                        backgroundColor: '#fff9e6',
                        color: '#f57c00',
                        borderRadius: '4px',
                        fontSize: '12px',
                      }}>{quiz.difficulty}</span>
                    </div>
                    <p style={{ fontSize: '16px', fontWeight: '500', color: '#333', marginBottom: '0.5rem' }}>
                      <ClickableText text={quiz.question_en} />
                    </p>
                    <p style={{ fontSize: '14px', color: '#666', marginBottom: '0.75rem' }}>
                      {quiz.question_native}
                    </p>
                    {quiz.options && quiz.options.length > 0 && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        {quiz.options.map((opt, optIdx) => (
                          <div key={optIdx} style={{
                            padding: '0.5rem 0.75rem',
                            marginBottom: '0.25rem',
                            backgroundColor: '#fafafa',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                          }}>
                            <span style={{ fontSize: '14px', color: '#333' }}>
                              {String.fromCharCode(65 + optIdx)}. <ClickableText text={opt.text} />
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{
                      padding: '0.75rem',
                      backgroundColor: '#f0fff4',
                      border: '1px solid #4CAF50',
                      borderRadius: '6px',
                    }}>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: '#2e7d32', marginBottom: '0.25rem' }}>
                        ✓ {Array.isArray(quiz.correct_answer) ? quiz.correct_answer.join(', ') : quiz.correct_answer}
                      </p>
                      <p style={{ fontSize: '13px', color: '#333' }}>{quiz.explanation_en}</p>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Complete vocabulary list */}
          {draft.vocabulary && draft.vocabulary.length > 0 && (
            <details style={{ marginBottom: '2rem' }}>
              <summary style={{
                cursor: 'pointer',
                padding: '1rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '14px',
              }}>
                {t('creator:workflow.stage2.completeVocabList', { count: draft.vocabulary.length })}
              </summary>
              <div style={{ marginTop: '1rem', maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>{t('creator:workflow.stage2.word')}</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>{t('creator:workflow.stage2.difficulty')}</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>{t('creator:workflow.stage2.level')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {draft.vocabulary
                      .sort((a, b) => (b.difficulty || 0) - (a.difficulty || 0))
                      .map((vocab, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '0.75rem' }}>
                            <strong>{vocab.word}</strong>
                            {'lemma' in vocab && vocab.lemma && vocab.lemma !== vocab.word && (
                              <span style={{ color: '#666', fontSize: '12px', marginLeft: '0.5rem' }}>
                                ({vocab.lemma})
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            <span style={{
                              padding: '4px 8px',
                              backgroundColor: getDifficultyColor(vocab.difficulty || 5) + '30',
                              color: getDifficultyColor(vocab.difficulty || 5),
                              borderRadius: '4px',
                              fontWeight: 'bold',
                              fontSize: '13px',
                            }}>
                              {vocab.difficulty?.toFixed(1) || 'N/A'}
                            </span>
                          </td>
                          <td style={{ padding: '0.75rem', color: '#666' }}>
                            {getDifficultyLabel(vocab.difficulty || 5)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {/* Navigation buttons (only show when not actively parsing) */}
          {!isParsingInProgress && (
            <div className="form-actions" style={{ marginTop: '2rem' }}>
              <button onClick={onGoBackStage} className="btn-secondary" disabled={loading}>
                {t('common:back')}
              </button>
              <button onClick={onAdvanceStage} className="btn-primary" disabled={loading}>
                {t('common:next')}
              </button>
            </div>
          )}
        </div>
      )}

      {loading && !isParsingInProgress && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>{t('creator:workflow.stage2.analyzing')}</p>
        </div>
      )}

      {/* Re-parse Confirmation Dialog */}
      {showReParseConfirm && (
        <div className="modal-overlay" onClick={cancelReParse}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: '1rem', color: '#333' }}>
              🤖 {t('creator:workflow.stage2.reParseTitle')}
            </h3>
            <p style={{ marginBottom: '1.5rem', color: '#666', lineHeight: '1.6' }}>
              {t('creator:workflow.stage2.reParseDescription')}
            </p>
            <div style={{
              backgroundColor: '#f0f8ff',
              border: '2px solid #667eea',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem',
            }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#333', fontWeight: 'bold' }}>
                🎁 {t('creator:workflow.stage2.reParseWillGet')}
              </p>
              <ul style={{ margin: '0.75rem 0 0 1.5rem', fontSize: '14px', color: '#555', lineHeight: '1.8' }}>
                <li>📝 <strong>{t('creator:workflow.stage2.reParseItem1Label')}</strong> {t('creator:workflow.stage2.reParseItem1Desc')}</li>
                <li>📚 <strong>{t('creator:workflow.stage2.reParseItem2Label')}</strong> {t('creator:workflow.stage2.reParseItem2Desc')}</li>
                <li>🎯 <strong>{t('creator:workflow.stage2.reParseItem3Label')}</strong> {t('creator:workflow.stage2.reParseItem3Desc')}</li>
                <li>💡 <strong>{t('creator:workflow.stage2.reParseItem4Label')}</strong> {t('creator:workflow.stage2.reParseItem4Desc')}</li>
              </ul>
            </div>
            <div className="modal-actions">
              <button onClick={cancelReParse} className="btn-secondary" disabled={loading}>
                {t('common:cancel')}
              </button>
              <button
                onClick={confirmReParse}
                className="btn-primary"
                disabled={loading}
                style={{ background: '#667eea' }}
              >
                {t('creator:workflow.stage1.generateContent')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stage2Parsing;
